import { useMemo, useState } from 'react';
import { AlertTriangle, Plus, RefreshCw } from 'lucide-react';
import { useCamions, useDeleteVoyage, useMines, useUpdateVoyage, useVoyages } from '@/hooks/useTransport';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';
import { SaisieVoyageDialog } from '@/components/operations/transport/SaisieVoyageDialog';
import { STATUT_VOYAGE_COLORS, STATUT_VOYAGE_LABELS, type StatutVoyage, type Voyage } from '@/types';
import { formatNumber } from '@/utils/currency';
import { can } from '@/utils/permissions';

const todayIso = () => new Date().toISOString().slice(0, 10);

const fmtHeure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });

export const RotationsPage = () => {
  const role = useAuthStore((s) => s.user?.role);

  const [date, setDate]                 = useState(todayIso());
  const [mineId, setMineId]             = useState('');
  const [camionId, setCamionId]         = useState('');
  const [statut, setStatut]             = useState<StatutVoyage | ''>('');
  const [search, setSearch]             = useState('');
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [editVoyage, setEditVoyage]     = useState<Voyage | undefined>(undefined);

  const { data: voyagesData, isLoading, refetch } = useVoyages({
    date,
    mineId:   mineId   || undefined,
    camionId: camionId || undefined,
    statut:   statut   || undefined,
    search:   search   || undefined,
    limit:    200,
  });
  const { data: minesData }   = useMines({ actif: true, limit: 100 });
  const { data: camionsData } = useCamions({ actif: true, limit: 500 });
  const update = useUpdateVoyage();
  const remove = useDeleteVoyage();

  const voyages = voyagesData?.data ?? [];
  const mines   = minesData?.data   ?? [];
  const camions = camionsData?.data ?? [];

  const canSaisir = can(role, 'operations.manage');
  const canEdit   = can(role, 'operations.manage');
  const canDelete = role === 'admin_holding' || role === 'daf' || role === 'manager';

  // Bande résumé
  const summary = useMemo(() => {
    const nonAnnule = voyages.filter((v) => v.statut !== 'annule');
    const tonnes    = nonAnnule.reduce((s, v) => s + parseFloat(v.tonnageCharge), 0);
    const camionsUniques = new Set(nonAnnule.map((v) => v.camionId)).size;
    const usd = tonnes * 15;
    return { voyages: nonAnnule.length, tonnes, camions: camionsUniques, usd };
  }, [voyages]);

  const onAnnuler = async (v: Voyage) => {
    const raison = await demander.texte({ titre: `Annuler le voyage ${v.numero} ?`, libelle: 'Indiquer la raison', confirmer: 'Annuler le voyage', danger: true });
    if (!raison) return;
    await update.mutateAsync({ id: v.id, payload: { statut: 'annule', raisonAnnulation: raison } });
  };

  const onSupprimer = async (v: Voyage) => {
    if (!(await demander.confirmer({ titre: `Supprimer définitivement le voyage ${v.numero} ?`, confirmer: 'Supprimer', danger: true }))) return;
    await remove.mutateAsync(v.id);
  };

  return (
    <div className="ds-content">
      {/* Topbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h1 style={{ fontFamily: 'var(--font-t)', fontSize: 22, fontWeight: 700, color: 'var(--p-deep)', margin: 0, flex: 1 }}>
          Rotations du jour
        </h1>
        <button type="button" onClick={() => refetch()} className="ds-btn ds-btn-g" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Actualiser
        </button>
        {canSaisir && (
          <button type="button" onClick={() => { setEditVoyage(undefined); setDialogOpen(true); }} className="ds-btn ds-btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Saisir un voyage
          </button>
        )}
      </div>

      {/* Filtres */}
      <div className="ds-bp" style={{ marginBottom: 14 }}>
        <div className="ds-bp-b" style={{ padding: 12 }}>
          <div className="grid grid-cols-5 gap-3" style={{ alignItems: 'end' }}>
            <Field label="Date">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Mine">
              <select value={mineId} onChange={(e) => setMineId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Toutes —</option>
                {mines.map((m) => <option key={m.id} value={m.id}>{m.code} · {m.nom}</option>)}
              </select>
            </Field>
            <Field label="Camion">
              <select value={camionId} onChange={(e) => setCamionId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {camions.map((c) => <option key={c.id} value={c.id}>{c.code} {c.immatriculation ? `· ${c.immatriculation}` : ''}</option>)}
              </select>
            </Field>
            <Field label="Statut">
              <select value={statut} onChange={(e) => setStatut(e.target.value as StatutVoyage | '')} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Tous —</option>
                {(Object.keys(STATUT_VOYAGE_LABELS) as StatutVoyage[]).map((s) => (
                  <option key={s} value={s}>{STATUT_VOYAGE_LABELS[s]}</option>
                ))}
              </select>
            </Field>
            <Field label="Recherche">
              <input value={search} onChange={(e) => setSearch(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="N° voyage / bon pesée / chauffeur" />
            </Field>
          </div>
        </div>
      </div>

      {/* Bande résumé */}
      <div className="ds-kpi-row" style={{ marginBottom: 14, gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="ds-kr"><div className="ds-kr-ico">🔄</div><div className="ds-kr-val">{summary.voyages}</div><div className="ds-kr-lbl">Voyages (hors annulés)</div></div>
        <div className="ds-kr"><div className="ds-kr-ico">⚖️</div><div className="ds-kr-val">{formatNumber(summary.tonnes)} T</div><div className="ds-kr-lbl">Tonnes cumulées</div></div>
        <div className="ds-kr"><div className="ds-kr-ico">🚛</div><div className="ds-kr-val">{summary.camions}</div><div className="ds-kr-lbl">Camions actifs</div></div>
        <div className="ds-kr"><div className="ds-kr-ico">💵</div><div className="ds-kr-val">${formatNumber(summary.usd)}</div><div className="ds-kr-lbl">Revenu estimé ({summary.tonnes.toFixed(1)} T × $15)</div></div>
      </div>

      {/* Table voyages */}
      <div className="ds-bp">
        <div className="ds-bp-b">
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
          ) : voyages.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>
              <AlertTriangle size={18} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Aucun voyage pour ces filtres
            </div>
          ) : (
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Départ</th>
                  <th>Camion</th>
                  <th>Chauffeur</th>
                  <th>Mine</th>
                  <th className="ds-am">Tonnes</th>
                  <th>N° pesée</th>
                  <th>Retour port</th>
                  <th className="ds-am">Km</th>
                  <th>Statut</th>
                  <th className="ds-am">Actions</th>
                </tr>
              </thead>
              <tbody>
                {voyages.map((v) => {
                  const km = (v.kmRetour && v.kmDepart) ? v.kmRetour - v.kmDepart : null;
                  return (
                    <tr key={v.id}>
                      <td><span className="ds-ref-code">{v.numero}</span></td>
                      <td style={{ fontFamily: 'var(--font-m)', fontSize: 11 }}>{fmtHeure(v.heureDepartMine)}</td>
                      <td style={{ fontSize: 12 }}>
                        <strong>{v.camion?.code ?? '—'}</strong>
                        {v.camion?.immatriculation && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{v.camion.immatriculation}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>{v.chauffeurNom}</td>
                      <td style={{ fontSize: 12 }}>
                        <strong>{v.mine?.code ?? '—'}</strong>
                        <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>→ {v.portDestination}</div>
                      </td>
                      <td className="ds-am" style={{ fontWeight: 700, color: 'var(--p)' }}>{parseFloat(v.tonnageCharge).toFixed(1)}</td>
                      <td style={{ fontSize: 11, fontFamily: 'var(--font-m)' }}>{v.numeroBonPesee}</td>
                      <td style={{ fontFamily: 'var(--font-m)', fontSize: 11 }}>
                        {v.heureArriveePort ? fmtHeure(v.heureArriveePort) : <span style={{ color: 'var(--tx-3)' }}>—</span>}
                      </td>
                      <td className="ds-am" style={{ fontSize: 11 }}>{km !== null ? `${km}` : '—'}</td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                          fontSize: 10, fontWeight: 700, color: 'white',
                          background: STATUT_VOYAGE_COLORS[v.statut],
                        }}>
                          {STATUT_VOYAGE_LABELS[v.statut]}
                        </span>
                      </td>
                      <td className="ds-am" style={{ whiteSpace: 'nowrap' }}>
                        {canEdit && v.statut !== 'annule' && (
                          <button type="button" className="ds-btn ds-btn-xs ds-btn-g" onClick={() => { setEditVoyage(v); setDialogOpen(true); }}>Éditer</button>
                        )}
                        {canEdit && v.statut !== 'annule' && (
                          <button type="button" className="ds-btn ds-btn-xs ds-btn-g" style={{ marginLeft: 4 }} onClick={() => onAnnuler(v)}>Annuler</button>
                        )}
                        {canDelete && (
                          <button type="button" className="ds-btn ds-btn-xs ds-btn-g" style={{ marginLeft: 4, color: '#dc2626' }} onClick={() => onSupprimer(v)}>Supp.</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <SaisieVoyageDialog open={dialogOpen} onClose={() => setDialogOpen(false)} voyage={editVoyage} />
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
